import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiRefreshCw, FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { dashboardApi } from '../../api';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 250;

type PositionItem = {
  connectionId: string;
  userId?: string;
  connectionType?: string;
  brokerageName?: string;
  productType?: string;
  symbol: string;
  side: string;
  qty: number;
  userName: string;
  userEmail?: string;
  avgEntryPrice?: number;
  unrealizedPl?: number;
  unrealizedPlPercent?: number;
  marketValue?: number;
};

const getBrokerLabel = (position: PositionItem) =>
  position.brokerageName ||
  (position.connectionType === 'snaptrade'
    ? 'SnapTrade'
    : position.connectionType === 'oanda'
      ? 'OANDA'
      : position.connectionType === 'genesis'
        ? 'GenesisFX'
        : '—');

type MasterFollowersOpenPositionsProps = {
  selectedBrokerId?: string | null;
};

const MasterFollowersOpenPositions: React.FC<MasterFollowersOpenPositionsProps> = ({ selectedBrokerId }) => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const forceRefreshOpenPositionsRef = useRef(false);
  const didInitialBrokerPullRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard', 'followers-open-positions', selectedBrokerId || 'all', page, debouncedSearch],
    queryFn: () => {
      if (!didInitialBrokerPullRef.current) {
        didInitialBrokerPullRef.current = true;
      }
      forceRefreshOpenPositionsRef.current = false;
      return dashboardApi.getFollowersOpenPositions({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        forceRefresh: true,
        brokerId: selectedBrokerId || undefined,
      });
    },
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const rowId = (p: PositionItem) =>
    `${p.userId || 'unknown'}-${p.connectionId}-${String(p.connectionType ?? 'oanda').toLowerCase()}-${p.symbol}-${p.side}`;

  const positions = (data?.positions ?? []) as PositionItem[];
  const pagination = data?.pagination ?? { page: 1, limit: PAGE_SIZE, total: 0, pages: 1 };

  if (isLoading && positions.length === 0) {
    return <Loader message="Loading followers' open positions..." />;
  }

  if (error) {
    return <ErrorDisplay error="Failed to load followers' open positions" />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="lg:text-4xl text-2xl title-page">Followers' Open Positions</h2>
          <p className="text-muted lg:mt-2 mt-1 lg:text-lg text-[14px]">
            Live open positions for your active followers (OANDA, SnapTrade/Webull, and GenesisFX), fetched directly from broker connections.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            forceRefreshOpenPositionsRef.current = true;
            void refetch();
          }}
          className="flex shrink-0 self-start sm:self-auto items-center gap-2 px-4 py-2 bg-surface border border-border rounded-lg text-foreground hover:bg-surface-elevated transition-colors"
        >
          <FiRefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="card">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted" />
          <input
            type="text"
            placeholder="Search by follower, email, symbol, or broker..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (setDebouncedSearch(searchQuery.trim()), setPage(1))}
            className="w-full pl-10 pr-4 py-2.5 bg-surface border border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="card min-w-0 overflow-x-auto">
        {positions.length === 0 ? (
          <div className="text-center py-12 text-muted">
            {pagination.total === 0
              ? 'No open positions from your followers (OANDA, SnapTrade/Webull, or GenesisFX).'
              : 'No positions match your search.'}
          </div>
        ) : (
          <>
            <div className="table-scroll">
            <table className="w-full min-w-[880px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Follower</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Broker</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Symbol</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Side</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Quantity</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Avg Entry</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Market Value</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Unrealized P&L</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Unrealized P&L %</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => {
                  const qtyDisplay = p.qty < 0 ? Math.abs(p.qty) : p.qty;
                  const brokerLabel = getBrokerLabel(p);
                  return (
                    <tr key={rowId(p)} className="border-b border-border hover:bg-surface-elevated transition-colors">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-foreground">{p.userName}</p>
                          {/* <p className="text-xs text-muted">{p.userEmail}</p> */}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground">{brokerLabel}</td>
                      <td className="py-3 px-4 text-xs text-muted capitalize">{p.productType || '—'}</td>
                      <td className="py-3 px-4 font-medium text-foreground">{p.symbol}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${p.side === 'long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}
                        >
                          {p.side}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-foreground">{qtyDisplay.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-foreground">
                        {p.avgEntryPrice != null ? Number(p.avgEntryPrice).toFixed(5) : '–'}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground">
                        {p.marketValue != null
                          ? `$${Number(p.marketValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                          : '–'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span
                          className={`font-semibold ${(p.unrealizedPl ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}
                        >
                          {p.unrealizedPl != null ? `$${Number(p.unrealizedPl).toFixed(2)}` : '–'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span
                          className={`font-semibold ${(p.unrealizedPlPercent ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}
                        >
                          {p.unrealizedPlPercent != null ? `${Number(p.unrealizedPlPercent).toFixed(2)}%` : '–'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {pagination.pages > 1 && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4 pt-4 border-t border-border">
                <div className="text-sm text-muted">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((pg) => Math.max(1, pg - 1))}
                    disabled={pagination.page <= 1}
                    className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FiChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="px-2 sm:px-4 py-2 text-sm font-medium text-foreground whitespace-nowrap">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((pg) => Math.min(pagination.pages, pg + 1))}
                    disabled={pagination.page >= pagination.pages}
                    className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FiChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MasterFollowersOpenPositions;
